/**
 * Text surgery for the editable transcript.
 *
 * A transcript that came back wrong is the single most common thing a user
 * wants to fix, and the fix is nearly always ONE word: a puck name heard as a
 * different puck name, a well number heard as another. So the correction path
 * is word-shaped — tap the wrong word, type the right one — and this module is
 * the part of that path with no DOM in it: given the sentence and which word
 * index was edited, produce the sentence to send back for a re-parse.
 *
 * Two rules drive everything here:
 *
 *   1. Positions are indices into the ORIGINAL string, not into a rebuilt copy.
 *      The component paints the words the user taps; if the offsets it renders
 *      from and the offsets the splice uses ever disagree, an edit silently
 *      lands on the neighbouring word. One tokenizer, used by both.
 *
 *   2. Punctuation stays with its word. "VP14." is one token, because a user
 *      correcting the puck name wants to type "VP15" and get "VP15." back, not
 *      to have to notice and re-type a full stop that was never wrong.
 *
 * Nothing here talks to the network. `reparseRequest` only shapes the body;
 * the dock owns the POST to `/api/v1/voice/parse`.
 */

/**
 * The server's own cap on a transcript it will parse. Enforced here so an
 * over-long sentence is truncated before it is sent rather than refused after.
 */
export const MAX_TRANSCRIPT_CHARS = 500;

/**
 * Split a sentence into whitespace-separated words, keeping each word's exact
 * span in the source string.
 *
 * @param {string} text
 * @returns {Array<{i: number, word: string, start: number, end: number}>}
 *   `start` inclusive, `end` exclusive, so `text.slice(start, end) === word`.
 *   Anything that is not a non-empty string tokenizes to nothing rather than
 *   throwing: the component renders this straight into a `v-for`.
 */
export function tokenize(text) {
  if (typeof text !== "string" || text === "") return [];

  // Built per call on purpose. A module-level /g regex carries `lastIndex`
  // between calls, and one early return would leave the next caller reading
  // from the middle of a different sentence.
  const word = /\S+/g;
  const tokens = [];
  let match = word.exec(text);
  while (match !== null) {
    tokens.push({
      i: tokens.length,
      word: match[0],
      start: match.index,
      end: match.index + match[0].length,
    });
    match = word.exec(text);
  }
  return tokens;
}

/**
 * Collapse runs of whitespace to a single space.
 *
 * The output of an edit is going to a parser, and a doubled space is the kind
 * of difference that changes a request hash without changing a meaning.
 *
 * @param {string} text
 * @returns {string}
 */
function collapseSpaces(text) {
  return text.replace(/\s{2,}/g, " ");
}

/**
 * Replace the i-th word of `text` with `replacement`.
 *
 * @param {string} text        the sentence as heard.
 * @param {number} i           index from `tokenize`.
 * @param {string} replacement what the user typed; trimmed before use. An
 *   empty (or whitespace-only) replacement DELETES the word, taking one
 *   adjacent space with it so the sentence does not keep the gap. A
 *   replacement containing a space is allowed and simply becomes two words —
 *   "VP14" corrected to "VP14 well 3" is a legitimate correction, not a typo
 *   to defend against.
 * @returns {string} the corrected sentence. An index with no token returns the
 *   input unchanged, which is what a stale tap after the transcript moved on
 *   should do.
 */
export function replaceWord(text, i, replacement) {
  const source = typeof text === "string" ? text : "";
  const token = tokenize(source)[i];
  if (!token) return source;

  const word = typeof replacement === "string" ? replacement.trim() : "";
  let before = source.slice(0, token.start);
  let after = source.slice(token.end);

  if (word === "") {
    // A deleted word leaves two spaces where there was one. Take the space
    // that FOLLOWED it if there is one; the space in front is the only one a
    // last word has, and a first word has neither.
    if (/^\s/.test(after)) after = after.slice(1);
    else if (/\s$/.test(before)) before = before.slice(0, -1);
  }

  return collapseSpaces(before + word + after);
}

/**
 * Shape the body for a re-parse of a corrected sentence.
 *
 * @param {string} text        the corrected sentence.
 * @param {string|null} screenPuck the puck the user is looking at, or null.
 * @returns {{transcript: string, screenPuck: string|null}} trimmed and capped
 *   at {@link MAX_TRANSCRIPT_CHARS}. `screenPuck` is null-coalesced because the
 *   server distinguishes "no puck on screen" from an empty string, and an
 *   empty string is what an unset ref reads as.
 */
export function reparseRequest(text, screenPuck) {
  const source = typeof text === "string" ? text : "";
  return {
    transcript: source.trim().slice(0, MAX_TRANSCRIPT_CHARS),
    screenPuck: screenPuck || null,
  };
}
